/**
 * Cultural Context Selector
 *
 * Radio card grid for selecting cultural baseline context.
 * Adjusts pragmatic signal interpretation per cultural norms.
 */

import { motion } from 'framer-motion'
import type { CulturalContext } from '@personic/shared'

interface CulturalContextSelectorProps {
  contexts: CulturalContext[]
  value: string | null
  onChange: (context: string | null) => void
  isLoading?: boolean
}

const regionIcons: Record<string, string> = {
  global: '🌍',
  east_asia: '🏯',
  south_asia: '🕌',
  middle_east: '🕋',
  latin_america: '🌎',
  northern_europe: '🏔️',
  western_europe: '🏰',
  north_america: '🗽',
}

export default function CulturalContextSelector({
  contexts,
  value,
  onChange,
  isLoading,
}: CulturalContextSelectorProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Cultural Context</h3>
        <p className="text-sm text-gray-500 mt-1">
          Select a cultural context to adjust baseline signal expectations
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* None option */}
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onChange(null)}
          className={`text-left p-4 rounded-xl border-2 transition-colors ${
            value === null
              ? 'border-engage-primary bg-engage-primary/5 ring-2 ring-engage-primary/20'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <div className="text-2xl mb-2">🌐</div>
          <h4 className="font-medium text-gray-900 text-sm">No Adjustment</h4>
          <p className="text-xs text-gray-500 mt-1">
            Use standard global baseline without cultural calibration
          </p>
        </motion.button>

        {contexts.map((ctx) => {
          const isSelected = value === ctx.name
          const icon = regionIcons[ctx.region] || '🌍'
          return (
            <motion.button
              key={ctx.name}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChange(ctx.name)}
              className={`text-left p-4 rounded-xl border-2 transition-colors ${
                isSelected
                  ? 'border-engage-primary bg-engage-primary/5 ring-2 ring-engage-primary/20'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{icon}</span>
                <span className="text-xs text-gray-400 uppercase tracking-wide">
                  {ctx.region.replace(/_/g, ' ')}
                </span>
              </div>
              <h4 className="font-medium text-gray-900 text-sm">
                {ctx.description}
              </h4>
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(ctx.adjustments).slice(0, 3).map(([key, val]) => (
                  <span
                    key={key}
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      isSelected
                        ? 'bg-engage-primary/10 text-engage-primary'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {key}: {val}
                  </span>
                ))}
              </div>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
